import { useEffect, useMemo } from 'react';
import AppRoutes from './routes/AppRoute';
import { App as AntApp, ConfigProvider, theme } from 'antd';
import arEG from 'antd/locale/ar_EG';
import enUS from 'antd/locale/en_US';
import { BrowserRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import themeDarkConfig from './theme/themeDarkConfig';
import themeLightConfig from './theme/themeLightConfig';
import { isDarkThemeMode } from './theme/themeMode';
import { themeMobile } from './theme/themeGlobal';
import useIsMobile from './hooks/useIsMobile';
import { COLOR_OPTIONS } from './components/selects/SelectPrimaryColor';
import { NotificationProvider } from './utils/NotificationService';
import LanguageRouteSync from './routes/LanguageRouteSync';
import {
  DEFAULT_LANGUAGE,
  getSupportedLanguage,
  isRtlLanguage,
  LANGUAGE,
} from './i18nLanguages';
import './styles/App.css';

function App() {
  const { i18n } = useTranslation();
  const themeMode = useSelector((state) => state.currentUser?.themeMode);
  const isDark = isDarkThemeMode(themeMode);
  const primaryColor = useSelector((state) => state.currentUser?.primaryColor);
  const primaryHex = COLOR_OPTIONS.find((it) => it.value === primaryColor)?.hex;
  const language =
    getSupportedLanguage(i18n.resolvedLanguage || i18n.language) ||
    DEFAULT_LANGUAGE;
  const antdLocale = language === LANGUAGE.AR ? arEG : enUS;
  const isMobile = useIsMobile();

  useEffect(() => {
    const nextTheme = isDark ? 'dark' : 'light';
    const themeColor = document.querySelector('meta[name="theme-color"]');

    document.documentElement.dataset.theme = nextTheme;
    document.documentElement.style.colorScheme = nextTheme;
    themeColor?.setAttribute('content', isDark ? '#07101f' : '#f5f7fb');
  }, [isDark]);

  useEffect(() => {
    // The password eye is handled by visibilityToggle.tabIndex in the inputs.
    const SKIP = 'button.ant-input-clear-icon:not([tabindex])';
    const skipTab = () =>
      document.querySelectorAll(SKIP).forEach((el) => el.setAttribute('tabindex', '-1'));
    skipTab();
    const observer = new MutationObserver(skipTab);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const themeApp = useMemo(
    () => ({
      algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
      token: {
        ...(isDark ? themeDarkConfig.token : themeLightConfig.token),
        ...(primaryHex ? { colorPrimary: primaryHex } : {}),
      },
      components: isDark
        ? themeDarkConfig.components
        : themeLightConfig.components,
    }),
    [isDark, primaryHex]
  );

  return (
    <ConfigProvider
      theme={themeApp}
      locale={antdLocale}
      direction={isRtlLanguage(language) ? 'rtl' : 'ltr'}
    >
      <ConfigProvider theme={isMobile ? themeMobile : undefined}>
        <AntApp>
          <NotificationProvider>
            <BrowserRouter
              future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
            >
              <LanguageRouteSync />
              <AppRoutes />
            </BrowserRouter>
          </NotificationProvider>
        </AntApp>
      </ConfigProvider>
    </ConfigProvider>
  );
}

export default App;
